import _ from 'lodash';
import {
  FINISH_FLAG,
  RECOVERY_FLAG,
  USER_ROLES,
  USER_ROLES_IDS,
} from './constants';
import LocalStorageService from '../services/utilities/LocalStorageService';
import SafetyDiariesConstants from './safetyDiariesConstants';

const hasRole = (userRoleId, approvedRoles) =>
  _.some(approvedRoles, role => userRoleId === role.id);

const rejectRoles = rejectRoles =>
  _.differenceWith(USER_ROLES, rejectRoles, _.isEqual);

export const RoleUtility = {
  rejectRoles,
  hasRole,
};

export default RoleUtility;

/**
 * 存在する権限名称(英語)かどうかを判定
 *
 * @param {string} roleName
 * @returns {boolean}
 */
const isUserRoleName = roleName => {
  const {
    system,
    prime,
    branch,
    manager,
    partner,
    foreman,
    operator,
    driver,
  } = USER_ROLES;
  if (!_.isString(roleName)) {
    throw new Error(`isUserRoleName args(roleName):${roleName}`);
  }
  return [system, prime, branch, manager, partner, foreman, operator, driver]
    .map(value => value.key)
    .includes(roleName);
};

/**
 * 存在するロールIDかを判定
 *
 * @param {string | number} roleId
 * @returns {boolean}
 */
const isUserRoleId = roleId => {
  if (!(_.isString(roleId) || _.isNumber(roleId))) {
    throw new Error(`isUserRoleId args(roleId):${roleId}`);
  }
  return isUserRoleName(roleIdToRoleName(roleId));
};

/**
 * 権限IDから権限名称(英語名)を取得する
 * 取得できない場合は例外発生
 */
export const roleIdToRoleName = id => {
  if (_.isString(id)) {
    id = Number(id);
  }

  const roleObj = _.filter(USER_ROLES, item => {
    return id === item.id;
  });
  if (roleObj.length === 0) {
    throw new Error(`roleIdToJapaneseRoleName args id is incorrect.`);
  }
  return roleObj[0].key;
};

/**
 * 権限(ロール)IDから権限名称(日本語名)を取得する
 * 取得できない場合は例外発生
 *
 * @param {*} id 権限(ロール)ID
 * @returns 権限名称(日本語名)
 */
export const roleIdToJapaneseRoleName = id => {
  if (_.isString(id)) {
    id = Number(id);
  }

  const roleObj = _.filter(USER_ROLES, item => {
    return id === item.id;
  });
  if (roleObj.length === 0) {
    throw new Error(`roleIdToJapaneseRoleName args id is incorrect.`);
  }
  return roleObj[0].name;
};

/**
 * 権限名称(英語名)から権限(ロール)IDを取得する
 */
export const roleNameToRoleId = roleName => {
  const roleObj = _.filter(USER_ROLES, item => {
    return roleName === item.key;
  });
  if (roleObj.length === 0) {
    throw new Error(`roleNameToRoleId args roleName is incorrect.`);
  }
  return roleObj[0].id;
};

/**
 * drawingツールで許可されている権限かどうかを判断する
 */
export const isPermissionRole = roleName => {
  if (!isUserRoleName(roleName)) {
    throw new Error('isPermissionRole args roleName is incorrect.');
  }
  const { prime, branch, manager, partner, foreman } = USER_ROLES;
  return [prime, branch, manager, partner, foreman]
    .map(value => value.key)
    .includes(roleName);
};

/**
 *  ロール名が指定した権限の範囲内にあるかどうかを判定する
 *
 * @param {*} roleArray 想定するロールのオブジェクトの配列
 * @param {string} roleName 判定するロール名
 * @returns 想定通りならtrue
 */
export const isTargetRoleName = (roleArray, roleName) => {
  if (!isUserRoleName(roleName)) {
    throw new Error('isTargetRoleName args roleName is incorrect.');
  }
  return roleArray.map(value => value.key).includes(roleName);
};

/**
 * 指定の権限名称(英語名)が元請会社かどうかを判断する
 */
export const isPrimaryCompany = function(roleName) {
  return isTargetRoleName(
    [USER_ROLES.prime, USER_ROLES.branch, USER_ROLES.manager],
    roleName
  );
};

/**
 * 指定の権限名称(英語名)が協力会社かどうかを判断する
 */
export const isPartnerCompany = function(roleName) {
  return isTargetRoleName(
    [
      USER_ROLES.partner,
      USER_ROLES.foreman,
      USER_ROLES.operator,
      USER_ROLES.driver,
    ],
    roleName
  );
};

/**
 *  ロールIDが指定した権限の範囲内にあるかどうかを判定する
 *
 * @param {*} roleArray 想定するロールのオブジェクトの配列
 * @param {number|string} roleId 判定するロールID
 * @returns 想定通りならtrue
 */
export const isTargetRoleId = (
  roleArray,
  roleId = LocalStorageService.getRoleId()
) => {
  if (!isUserRoleId(roleId)) {
    throw new Error('isTargetRoleId args roleId is incorrect.');
  }
  return roleArray.map(value => value.id).includes(Number(roleId));
};

/**
 * 指定の権限IDが元請会社(本社管理者,支店管理者,現場監督)かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isPrimaryCompanyRoleId = roleId => {
  return isTargetRoleId(
    [USER_ROLES.prime, USER_ROLES.branch, USER_ROLES.manager],
    roleId
  );
};

/**
 * 指定の権限IDが協力会社(協力会社管理者,職長,作業員,ドライバー)かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isPartnerCompanyRoleId = roleId => {
  return isTargetRoleId(
    [
      USER_ROLES.partner,
      USER_ROLES.foreman,
      USER_ROLES.operator,
      USER_ROLES.driver,
    ],
    roleId
  );
};

/**
 * 指定の権限IDが本社管理者かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isPrimeRoleId = roleId => {
  return isTargetRoleId([USER_ROLES.prime], roleId);
};

/**
 * 指定の権限IDが支店管理者かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isBranchRoleId = roleId => {
  return isTargetRoleId([USER_ROLES.branch], roleId);
};

/**
 * 指定の権限IDが現場監督かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isManagerRoleId = roleId => {
  return isTargetRoleId([USER_ROLES.manager], roleId);
};

/**
 * 指定の権限IDが協力会社管理者かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isPartnerRoleId = roleId => {
  return isTargetRoleId([USER_ROLES.partner], roleId);
};

/**
 * 指定の権限IDが職長かどうかを判断する
 *
 * @param {number} roleId ロールID（number型）
 * @returns
 */
export const isForemanRoleId = roleId => {
  return isTargetRoleId([USER_ROLES.foreman], roleId);
};

/**
 * ログインユーザーが元請会社(本社管理者,支店管理者,現場監督)かどうかを判断する
 *
 * @returns ログインユーザーが元請会社ならtrue
 */
export const isLoginUserPrimaryCompany = () => {
  return isPrimaryCompanyRoleId(LocalStorageService.getRoleId());
};

/**
 * ログインユーザーが協力会社(協力会社管理者,職長,作業員,ドライバー)かどうかを判断する
 *
 * @returns ログインユーザーが協力会社ならtrue
 */
export const isLoginUserPartnerCompany = () => {
  return isPartnerCompanyRoleId(LocalStorageService.getRoleId());
};

/**
 * ログインユーザーが本社管理者かどうかを判断する
 *
 * @returns ログインユーザーが本社管理者ならtrue
 */
export const isLoginUserPrime = () => {
  return isPrimeRoleId(LocalStorageService.getRoleId());
};

/**
 * ログインユーザーが支店管理者かどうかを判断する
 *
 * @returns ログインユーザーが支店管理者ならtrue
 */
export const isLoginUserBranch = () => {
  return isBranchRoleId(LocalStorageService.getRoleId());
};

/**
 * ログインユーザーが現場監督かどうかを判断する
 *
 * @returns ログインユーザーが現場監督ならtrue
 */
export const isLoginUserManager = () => {
  return isManagerRoleId(LocalStorageService.getRoleId());
};
/**
 * ログインユーザーが協力会社管理者かどうかを判断する
 *
 * @returns ログインユーザーが協力会社管理者ならtrue
 */
export const isLoginUserPartner = () => {
  return isPartnerRoleId(LocalStorageService.getRoleId());
};

/**
 * ログインユーザーが職長かどうかを判断する
 *
 * @returns ログインユーザーが職長ならtrue
 */
export const isLoginUserForeman = () => {
  return isForemanRoleId(LocalStorageService.getRoleId());
};

const isPreviewSafetyDiary = safetyDiary => {
  // 協力会社ユーザの場合：自分ではないユーザーが作成した場合は参照のみ
  // ダッシュボードからはsafetyDiary.create_user_role_codeがnullになっている
  // 協力会社ユーザの場合は自分しか承認操作ができないのでuserIdの比較のみで良い
  return (
    isTargetRoleId(USER_ROLES_IDS.PARTNER_COMPANY) &&
    LocalStorageService.getUserId() !== safetyDiary.create_user_id
  );
};

export const canSafetyDiaryRemove = safetyDiary => {
  return !isPreviewSafetyDiary(safetyDiary);
};

export const canSafetyDiaryEdit = safetyDiary => {
  if (
    safetyDiary.recovery_flag === RECOVERY_FLAG.complete.id ||
    safetyDiary.finish_flag === FINISH_FLAG.complete.id
  ) {
    return false;
  }

  return !isPreviewSafetyDiary(safetyDiary);
};

export const canSafetyDiaryApprove = safetyDiary => {
  return !isPreviewSafetyDiary(safetyDiary);
};

export const getSafetyDiaryDetailPageName = safetyDiary => {
  if (
    safetyDiary.recovery_flag === RECOVERY_FLAG.complete.id ||
    safetyDiary.finish_flag === FINISH_FLAG.complete.id
  ) {
    return SafetyDiariesConstants.pageName.safetyDiariesConfirm;
  }

  return isPreviewSafetyDiary(safetyDiary)
    ? SafetyDiariesConstants.pageName.safetyDiariesConfirm
    : SafetyDiariesConstants.pageName.safetyDiariesRegister;
};

export const getRoleNameByRoleId = roleId => {
  const role = _.find(USER_ROLES, role => role.id === roleId);
  return _.get(role, 'name', '');
};



// WEBPACK FOOTER //
// ./src/utilities/roleUtility.js